import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Tag, Button, Empty } from 'antd';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import ReactECharts from 'echarts-for-react';
import { yuan } from '../utils/format.js';
import { apiFetch, onDataChanged } from '../utils/api.js';
import { BRAND, BRAND_RGB } from '../utils/chartTheme.js';
import AiStaffPanel from '../components/AiStaffPanel.jsx';

// 统计卡统一规范：白底 + 左侧语义色条 + 标题 + 大数字
function StatCard({ label, value, suffix, color, sub }) {
  return (
    <div className="stat-card" style={{ '--sc': color }}>
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ color }}>{value}{suffix && <span style={{ fontSize: 13, fontWeight: 400, color: '#8C8178', marginLeft: 4 }}>{suffix}</span>}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

// 工作提醒分级配色
const LEVEL_COLOR = { red: '#E24B4A', orange: '#EF9F27', yellow: '#BA7517', green: '#639922' };
const LEVEL_BG = { red: '#FCEBEB', orange: '#FAEEDA', yellow: '#FAEEDA', green: '#EAF3DE' };
const LEVEL_TEXT = { red: '#791F1F', orange: '#854F0B', yellow: '#633806', green: '#27500A' };

// 首页：工作台概览 + 核心指标卡 + 工作提醒/趋势 + AI 员工值班（分析图表见各专属页面）
export default function Dashboard() {
  const navigate = useNavigate();
  const [wb, setWb] = useState(null);          // /api/dashboard/workbench（今日/本月 + CRM + 提醒 + AI 播报）
  const [daily, setDaily] = useState(null);    // /api/dashboard（每日趋势，只取 daily 字段）

  const todayStr = dayjs().format('YYYY-MM-DD');
  const weekday = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'][dayjs().day()];

  // 工作台聚合数据（今日/本月 + CRM + 提醒 + AI 播报），数据变更时静默刷新
  useEffect(() => {
    const load = () => apiFetch('/api/dashboard/workbench').then(r => r.json()).then(d => { if (d.success) setWb(d); }).catch(() => {});
    load();
    return onDataChanged(load);
  }, []);

  // 每日趋势数据（总和口径，仅供趋势图）
  useEffect(() => {
    const load = () => apiFetch('/api/dashboard').then(r => r.json()).then(d => setDaily(d)).catch(() => {});
    load();
    return onDataChanged(load);
  }, []);

  if (!wb) {
    return (
      <div className="page-container">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 16, marginBottom: 20 }}>
          {Array.from({ length: 6 }).map((_, i) => <div key={i} className="skeleton-block" style={{ height: 96 }} />)}
        </div>
        <div className="skeleton-block" style={{ height: 320, marginBottom: 20 }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(320px,1fr))', gap: 16 }}>
          <div className="skeleton-block" style={{ height: 280 }} />
          <div className="skeleton-block" style={{ height: 280 }} />
        </div>
      </div>
    );
  }

  const { today, month, crmS } = { ...wb, crmS: wb.crm };
  const reminders = wb.reminders || [];
  const todoCount = wb.todo_count;

  // 营收与发货趋势（双轴：营收左轴¥珊瑚橙实线+面积，发货单数右轴单蓝色虚线+空心点，节点灰字标签）
  const dailyRows = ((daily && daily.daily) || []).slice(-30); // 近 30 天，避免标签过挤
  const dailyChartOption = {
    tooltip: { trigger: 'axis' },
    legend: { data: ['营收', '发货单数'] },
    grid: { left: '3%', right: '4%', top: 44, bottom: '3%', containLabel: true },
    xAxis: { type: 'category', data: dailyRows.map(d => d.shipping_date.slice(5)), boundaryGap: false },
    yAxis: [
      { type: 'value', name: '营收(元)', position: 'left', axisLabel: { color: BRAND }, splitLine: { show: true } },
      { type: 'value', name: '发货(单)', position: 'right', axisLabel: { color: '#378ADD' }, splitLine: { show: false } },
    ],
    series: [
      {
        name: '营收',
        type: 'line',
        yAxisIndex: 0,
        smooth: true,
        areaStyle: { color: `rgba(${BRAND_RGB}, 0.12)` },
        lineStyle: { width: 3, color: BRAND },
        itemStyle: { color: BRAND, borderColor: '#FFFFFF', borderWidth: 2 },
        symbol: 'circle',
        label: { show: true, position: 'top', color: '#A0857B', fontSize: 11, hideOverlap: true, formatter: (p) => p.value >= 10000 ? (p.value / 10000).toFixed(2) + '万' : p.value },
        data: dailyRows.map(d => d.revenue),
      },
      {
        name: '发货单数',
        type: 'line',
        yAxisIndex: 1,
        smooth: true,
        lineStyle: { width: 2.5, color: '#378ADD', type: 'dashed' },
        itemStyle: { color: '#FFFFFF', borderColor: '#378ADD', borderWidth: 2 },
        symbol: 'emptyCircle',
        symbolSize: 8,
        label: { show: true, position: 'bottom', color: '#7A9CC0', fontSize: 11, hideOverlap: true, formatter: (p) => p.value > 0 ? p.value + '单' : '' },
        data: dailyRows.map(d => d.order_count),
      },
    ],
  };

  return (
    <div className="page-container">
      {/* ① 工作台概览条（今日） */}
      <div style={{ background: 'var(--brand-soft)', border: '1px solid var(--brand-border)', borderRadius: 12, padding: '16px 20px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 500, color: '#712B13' }}>工作台 · {todayStr} {weekday}</div>
          <div style={{ fontSize: 13, color: 'var(--brand-strong)', marginTop: 4 }}>
            今日营收 <b>{'¥' + yuan(today.revenue)}</b> · 今日利润 <b>{'¥' + yuan(today.profit)}</b> · 待办 <b>{todoCount}</b> 项
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          {todoCount > 0 ? (
            <Button type="primary" onClick={() => document.getElementById('reminders-anchor') && document.getElementById('reminders-anchor').scrollIntoView({ behavior: 'smooth', block: 'center' })}>
              待办 {todoCount} 项
            </Button>
          ) : (
            <Tag color="green" style={{ fontSize: 13, padding: '4px 12px', marginRight: 0 }}>今日无事，经营健康</Tag>
          )}
        </div>
      </div>

      {/* ② 核心指标卡（今日 + 本月） */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={8} lg={4}>
          <StatCard label="今日营收" value={'¥' + yuan(today.revenue)} color="var(--brand)" sub={'利润率 ' + (today.margin * 100).toFixed(1) + '%'} />
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <StatCard label="今日利润" value={'¥' + yuan(today.profit)} color="#CF1322" sub={today.order_count + ' 单'} />
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <StatCard label="本月营收" value={'¥' + yuan(month.revenue)} color="var(--brand)" sub={month.month + ' 累计'} />
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <StatCard label="本月利润" value={'¥' + yuan(month.profit)} color="#CF1322" sub={'毛利率 ' + (month.avg_margin * 100).toFixed(1) + '%'} />
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <StatCard label="本月订单" value={month.order_count} suffix="单" color="#378ADD" sub={'今日 ' + today.order_count + ' 单'} />
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <StatCard label="库存金额" value={'¥' + yuan(wb.inv_amount)} color="#378ADD" sub="占用资金" />
        </Col>
      </Row>

      {/* ②b 客户管理指标卡（与上排同规格，数据源 /api/crm/stats 同口径） */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={8} lg={4}>
          <StatCard label="客户总数" value={crmS.customer_count} suffix="家" color="#7F77DD" sub="微信 / CRM 客户" />
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <StatCard label="本月成交额" value={'¥' + yuan(crmS.month_amount)} color="var(--brand)" sub={month.month + ' 客户订单'} />
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <StatCard label="本月成交利润" value={'¥' + yuan(crmS.month_profit)} color="#CF1322" sub="发货后利润" />
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <StatCard label="本月预估利润" value={'¥' + yuan(crmS.month_estimate_profit)} color="#3F8600" sub="含待发货" />
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <StatCard label="应收总额" value={'¥' + yuan(crmS.total_receivable)} color="#EF9F27" sub="客户待回款" />
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <StatCard label="待跟进" value={crmS.pending_followup} suffix="项" color="#378ADD" sub="客户跟进事项" />
        </Col>
      </Row>

      {/* ③ 工作提醒 + 每日趋势（两列拉伸等高，底边对齐） */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16, alignItems: 'stretch' }}>
        <Col xs={24} lg={14}>
          <Card title="今日工作提醒" style={{ borderRadius: 12, height: '100%' }} styles={{ body: { padding: 16 } }}>
            <div id="reminders-anchor" />
            {reminders.length === 0 ? (
              <Empty description="今日无事，经营健康" style={{ padding: 32 }} />
            ) : (
              reminders.map(r => {
                const hot = r.key === 'ordermatch';
                return (
                  <div key={r.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', marginBottom: 8, borderRadius: 8, background: hot ? '#FDEBEB' : (LEVEL_BG[r.level] || '#F1EFE8'), border: hot ? '1px solid #E24B4A' : '1px solid transparent' }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: hot ? '#E24B4A' : (LEVEL_COLOR[r.level] || '#888780'), flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: hot ? 700 : 500, color: hot ? '#A81111' : (LEVEL_TEXT[r.level] || '#444441') }}>
                        {r.title} {r.count}{r.amount != null ? ' · ¥' + yuan(r.amount) : ''}
                      </div>
                      <div style={{ fontSize: 12, color: hot ? '#A81111' : '#888780' }}>{r.sub}</div>
                    </div>
                    {hot
                      ? <Button size="small" type="primary" danger onClick={() => navigate(r.link)}>去补录</Button>
                      : <Button size="small" type="link" onClick={() => navigate(r.link)}>去处理 →</Button>}
                  </div>
                );
              })
            )}
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card title="营收与发货趋势" style={{ borderRadius: 12, height: '100%', display: 'flex', flexDirection: 'column' }}
            styles={{ body: { padding: 16, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } }}>
            <ReactECharts option={dailyChartOption} theme="brand" style={{ flex: 1, minHeight: 320 }} />
          </Card>
        </Col>
      </Row>

      {/* ④ AI 员工值班卡片区（就地追问 + 语音 + 团队会诊） */}
      <AiStaffPanel briefings={wb.briefings || {}} />
    </div>
  );
}
